import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { PaperPlaneTilt, EnvelopeSimple, CheckCircle, XCircle, Eye, FloppyDisk, Trash, PencilSimple, Clock, AddressBook } from "@phosphor-icons/react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { api, formatApiErrorDetail } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "./ui/alert-dialog";

const inputCls = "h-11 bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40";

const htmlToText = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  return (doc.body.textContent || "").trim();
};

export const NewsletterCard = () => {
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [recipients, setRecipients] = useState("");
  const [configured, setConfigured] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [scheduled, setScheduled] = useState([]);
  const [scheduling, setScheduling] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [lists, setLists] = useState([]);
  const [log, setLog] = useState([]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, false] }],
        ["bold", "italic", "underline"],
        [{ color: [] }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image: function () {
          const url = window.prompt("Incolla l'URL dell'immagine (deve essere online, es. https://...):");
          if (url) {
            const range = this.quill.getSelection(true);
            this.quill.insertEmbed(range.index, "image", url);
          }
        },
      },
    },
  }), []);

  const loadLog = () => api.get("/admin/marketing/newsletter/log").then(({ data }) => setLog(data)).catch(() => {});
  const loadDrafts = () => api.get("/admin/marketing/newsletter/drafts").then(({ data }) => setDrafts(data)).catch(() => {});
  const loadScheduled = () => api.get("/admin/marketing/newsletter/scheduled").then(({ data }) => setScheduled(data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings/smtp").then(({ data }) => {
      setConfigured(!!data.marketing?.host);
      setRecipients((prev) => prev || (data.marketing?.recipients || []).join(", "));
    }).catch(() => {});
    loadLog();
    loadDrafts();
    loadScheduled();
    api.get("/admin/marketing/lists").then(({ data }) => setLists(data)).catch(() => {});
  }, []);

  const addFromList = (l) => {
    const current = recipients.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean);
    const merged = [...new Set([...current, ...(l.emails || [])])];
    setRecipients(merged.join(", "));
    toast.success(`Aggiunti i contatti della lista "${l.name}" (${merged.length} totali)`);
  };

  const recipientList = recipients.split(/[,;\n]/).map((r) => r.trim()).filter(Boolean);
  const plainText = htmlToText(html);

  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post("/admin/marketing/newsletter/preview", { subject, html });
      setPreviewHtml(data.html);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPreviewing(false);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/admin/marketing/newsletter/drafts", {
        id: draftId, subject, html, recipients: recipientList,
      });
      setDraftId(data.id);
      toast.success(draftId ? "Bozza aggiornata" : "Bozza salvata");
      loadDrafts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingDraft(false);
    }
  };

  const handleLoadDraft = (d) => {
    setSubject(d.subject || "");
    setHtml(d.html || "");
    if ((d.recipients || []).length) setRecipients(d.recipients.join(", "));
    setDraftId(d.id);
    toast.info("Bozza caricata nell'editor");
  };

  const handleDeleteDraft = async (id) => {
    try {
      await api.delete(`/admin/marketing/newsletter/drafts/${id}`);
      if (id === draftId) setDraftId(null);
      toast.success("Bozza eliminata");
      loadDrafts();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSchedule = async () => {
    if (!scheduleAt) {
      toast.error("Scegli data e ora di invio");
      return;
    }
    setScheduling(true);
    try {
      const { data } = await api.post("/admin/marketing/newsletter/schedule", {
        subject, body: plainText, html, recipients: recipientList,
        scheduled_at: new Date(scheduleAt).toISOString(),
      });
      toast.success(data.message);
      setScheduleOpen(false);
      setScheduleAt("");
      setSubject("");
      setHtml("");
      if (draftId) {
        api.delete(`/admin/marketing/newsletter/drafts/${draftId}`).then(loadDrafts).catch(() => {});
        setDraftId(null);
      }
      loadScheduled();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setScheduling(false);
    }
  };

  const handleCancelScheduled = async (id) => {
    try {
      const { data } = await api.delete(`/admin/marketing/newsletter/scheduled/${id}`);
      toast.success(data.message);
      loadScheduled();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSend = async () => {
    setConfirming(false);
    setSending(true);
    try {
      const { data } = await api.post("/admin/marketing/newsletter", {
        subject, body: plainText, html, recipients: recipientList,
      });
      toast.success(data.message);
      setSubject("");
      setHtml("");
      if (draftId) {
        api.delete(`/admin/marketing/newsletter/drafts/${draftId}`).then(loadDrafts).catch(() => {});
        setDraftId(null);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
      loadLog();
    }
  };

  return (
    <div className="mb-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="newsletter-card">
      <EnvelopeSimple size={36} weight="duotone" className="text-amber-400 mb-4" />
      <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Invia Newsletter</h2>
      <p className="text-sm text-slate-400 mb-6">
        Componi la newsletter con formattazione, immagini e link: viene inviata con il logo e la grafica AREAPAQ
        tramite l'SMTP EmailMarketing. Ogni destinatario riceve un'email individuale.
      </p>
      {configured === false && (
        <p className="text-sm text-amber-400 mb-6" data-testid="newsletter-smtp-missing">
          SMTP EmailMarketing non configurato. Vai in <Link to="/impostazioni" className="underline underline-offset-2">Impostazioni</Link> e configura "SMTP_Marketing" per poter inviare.
        </p>
      )}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Oggetto</Label>
          <Input data-testid="newsletter-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder="Novità di questo mese da AREAPAQ" className={inputCls} />
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Contenuto</Label>
          <div className="flex flex-wrap items-center gap-2 mb-1" data-testid="newsletter-presets">
            <span className="text-[10px] tracking-[0.15em] uppercase text-white/35">Modelli:</span>
            {[
              ["Novità del mese", "<h2>Le novità di questo mese</h2><p>Ciao!</p><p>Ecco cosa c'è di nuovo:</p><ul><li><strong>Novità 1</strong> — breve descrizione</li><li><strong>Novità 2</strong> — breve descrizione</li><li><strong>Novità 3</strong> — breve descrizione</li></ul><p>A presto,<br/>Il team</p>"],
              ["Promozione", "<h2>Offerta speciale per te 🎁</h2><p>Solo per pochi giorni:</p><h3>SCONTO DEL 20%</h3><p>su tutti i nostri servizi. Approfittane subito!</p><p><a href='https://www.areapaq.it'>Scopri l'offerta →</a></p><p><em>Valida fino al 30 del mese.</em></p>"],
              ["Annuncio", "<h2>Un annuncio importante</h2><p>Gentile cliente,</p><p>ti scriviamo per informarti che…</p><blockquote>Il dettaglio più importante va qui, in evidenza.</blockquote><p>Per qualsiasi domanda siamo a disposizione.</p><p>Cordiali saluti</p>"],
              ["Invito evento", "<h2>Sei invitato! 🎉</h2><p>Ti aspettiamo al nostro evento:</p><ul><li><strong>Quando:</strong> data e ora</li><li><strong>Dove:</strong> indirizzo</li><li><strong>Cosa:</strong> descrizione dell'evento</li></ul><p><a href='https://www.areapaq.it'>Conferma la tua presenza →</a></p>"],
            ].map(([name, tpl]) => (
              <button key={name} type="button" data-testid={`newsletter-preset-${name.toLowerCase().replace(/ /g, "-")}`}
                onClick={() => { setHtml(tpl); toast.success(`Modello "${name}" caricato: personalizzalo`); }}
                className="h-7 px-3 rounded-full text-[11px] font-semibold border border-white/15 text-white/55 hover:text-white hover:border-amber-400/50 transition-colors">
                {name}
              </button>
            ))}
          </div>
          <div className="newsletter-editor" data-testid="newsletter-editor">
            <ReactQuill theme="snow" value={html} onChange={setHtml} modules={quillModules}
              placeholder="Scrivi qui il contenuto della newsletter..." />
          </div>
          <p className="text-[11px] text-white/40">Per le immagini usa il pulsante immagine della barra e incolla un URL online (es. dal tuo sito).</p>
        </div>
        <div className="space-y-2">
          <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Destinatari ({recipientList.length})</Label>
          <Textarea data-testid="newsletter-recipients-input" value={recipients} onChange={(e) => setRecipients(e.target.value)} rows={2}
            placeholder="a@x.it, b@x.it, c@x.it"
            className="bg-transparent border-white/10 text-white placeholder:text-white/25 focus:border-white/40 focus-visible:ring-1 focus-visible:ring-white/40 resize-y" />
          <p className="text-[11px] text-white/40">Separa più indirizzi con virgola, punto e virgola o a capo.</p>
          {lists.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="newsletter-lists-picker">
              <span className="text-[11px] text-white/40 inline-flex items-center gap-1"><AddressBook size={12} /> Aggiungi da lista:</span>
              {lists.map((l) => (
                <button key={l.id} type="button" data-testid={`newsletter-list-${l.id}`} onClick={() => addFromList(l)}
                  className="h-7 px-3 rounded-full text-[11px] font-semibold border border-violet-400/30 text-violet-300 hover:bg-violet-400/10 transition-colors">
                  {l.name} ({(l.emails || []).length})
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button data-testid="newsletter-save-draft-button" onClick={handleSaveDraft}
            disabled={savingDraft || (!subject.trim() && !plainText)}
            variant="ghost" className="h-11 px-6 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full font-semibold text-sm disabled:opacity-40">
            <FloppyDisk size={16} className="mr-1.5" /> {savingDraft ? "Salvataggio..." : draftId ? "Aggiorna bozza" : "Salva bozza"}
          </Button>
          <Button data-testid="newsletter-preview-button" onClick={handlePreview}
            disabled={previewing || !plainText}
            variant="ghost" className="h-11 px-6 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full font-semibold text-sm disabled:opacity-40">
            <Eye size={16} className="mr-1.5" /> {previewing ? "Caricamento..." : "Anteprima"}
          </Button>
          <Button data-testid="newsletter-schedule-button" onClick={() => setScheduleOpen(true)}
            disabled={!configured || !subject.trim() || !plainText || recipientList.length === 0}
            variant="ghost" className="h-11 px-6 border border-amber-400/40 text-amber-300 hover:bg-amber-400/10 hover:text-amber-200 rounded-full font-semibold text-sm disabled:opacity-40">
            <Clock size={16} className="mr-1.5" /> Programma
          </Button>
          <Button data-testid="newsletter-send-button" onClick={() => setConfirming(true)}
            disabled={sending || !configured || !subject.trim() || !plainText || recipientList.length === 0}
            className="h-11 px-6 bg-amber-400 text-black hover:bg-amber-300 rounded-full font-semibold text-sm disabled:opacity-40">
            <PaperPlaneTilt size={16} className="mr-1.5" /> {sending ? "Invio in corso..." : "Invia newsletter"}
          </Button>
        </div>
        {draftId && (
          <p className="text-[11px] text-amber-300/80 flex items-center gap-1.5" data-testid="newsletter-editing-draft">
            <PencilSimple size={12} /> Stai lavorando su una bozza salvata: inviandola verrà rimossa dalle bozze.
          </p>
        )}
      </div>

      {scheduled.length > 0 && (
        <div className="mt-8 pt-6 border-t border-white/5" data-testid="newsletter-scheduled">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">Newsletter programmate</h3>
          <div className="space-y-2">
            {scheduled.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2" data-testid={`newsletter-scheduled-${s.id}`}>
                {s.status === "pending" && <span className="flex items-center gap-1 text-amber-300 shrink-0"><Clock size={14} /> in attesa</span>}
                {s.status === "sending" && <span className="flex items-center gap-1 text-sky-300 shrink-0"><PaperPlaneTilt size={14} /> invio...</span>}
                {s.status === "sent" && <span className="flex items-center gap-1 text-emerald-400 shrink-0"><CheckCircle size={14} weight="fill" /> inviata</span>}
                {s.status === "failed" && <span className="flex items-center gap-1 text-red-400 shrink-0"><XCircle size={14} weight="fill" /> fallita</span>}
                <span className="text-white font-medium truncate max-w-[240px]" title={s.subject}>{s.subject}</span>
                <span className="text-slate-400 text-xs shrink-0">
                  {new Date(s.scheduled_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-500 text-xs">→ {(s.recipients || []).length} destinatari</span>
                {s.error && <span className="text-red-400/80 text-xs truncate max-w-[200px]" title={s.error}>{s.error}</span>}
                {s.status === "pending" && (
                  <Button data-testid={`newsletter-scheduled-cancel-${s.id}`} onClick={() => handleCancelScheduled(s.id)} variant="ghost"
                    className="ml-auto h-8 px-4 border border-red-500/30 text-red-400/80 hover:bg-red-500/10 hover:text-red-400 rounded-full text-xs font-semibold">
                    Annulla
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <div className="mt-8 pt-6 border-t border-white/5" data-testid="newsletter-drafts">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">Bozze salvate</h3>
          <div className="space-y-2">
            {drafts.map((d) => (
              <div key={d.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2" data-testid={`newsletter-draft-${d.id}`}>
                <span className={`font-medium truncate max-w-[260px] ${d.id === draftId ? "text-amber-300" : "text-white"}`} title={d.subject}>
                  {d.subject || "Senza oggetto"}
                </span>
                <span className="text-slate-400 text-xs shrink-0">
                  {d.updated_at && new Date(d.updated_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-500 text-xs">→ {(d.recipients || []).length} destinatari</span>
                <span className="ml-auto flex gap-2">
                  <Button data-testid={`newsletter-draft-load-${d.id}`} onClick={() => handleLoadDraft(d)} variant="ghost"
                    className="h-8 px-4 border border-white/15 text-white/70 hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
                    <PencilSimple size={13} className="mr-1" /> Riprendi
                  </Button>
                  <Button data-testid={`newsletter-draft-delete-${d.id}`} onClick={() => handleDeleteDraft(d.id)} variant="ghost"
                    className="h-8 w-8 p-0 rounded-full text-red-400/60 hover:text-red-400 hover:bg-red-500/10">
                    <Trash size={14} />
                  </Button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {log.length > 0 && (
        <div className="mt-8 pt-6 border-t border-white/5" data-testid="newsletter-log">
          <h3 className="font-heading text-lg font-semibold tracking-tight mb-3">Storico invii newsletter</h3>
          <div className="space-y-2">
            {log.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm border-b border-white/5 pb-2">
                {r.ok ? (
                  <span className="flex items-center gap-1 text-emerald-400 shrink-0"><CheckCircle size={14} weight="fill" /> inviata</span>
                ) : (
                  <span className="flex items-center gap-1 text-red-400 shrink-0"><XCircle size={14} weight="fill" /> fallita</span>
                )}
                <span className="text-slate-400 shrink-0">
                  {new Date(r.timestamp).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-white font-medium truncate max-w-[280px]" title={r.subject}>{r.subject}</span>
                <span className="text-slate-400 text-xs">→ {r.recipients.length} destinatari</span>
                {r.error && <span className="text-red-400/80 text-xs truncate max-w-[240px]" title={r.error}>{r.error}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={scheduleOpen} onOpenChange={setScheduleOpen}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-md" data-testid="newsletter-schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <Clock size={20} className="text-amber-400" /> Programma invio
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-sm text-slate-400">
              "{subject}" verrà inviata automaticamente a {recipientList.length} destinatari alla data e ora scelte (ora italiana).
            </p>
            <div className="space-y-2">
              <Label className="text-xs tracking-[0.2em] uppercase text-white/60">Data e ora di invio</Label>
              <Input data-testid="newsletter-schedule-datetime" type="datetime-local" value={scheduleAt}
                onChange={(e) => setScheduleAt(e.target.value)}
                min={new Date(Date.now() + 5 * 60000).toISOString().slice(0, 16)}
                className={`${inputCls} [color-scheme:dark]`} />
            </div>
            <Button data-testid="newsletter-schedule-confirm" onClick={handleSchedule} disabled={scheduling || !scheduleAt}
              className="h-11 px-6 bg-amber-400 text-black hover:bg-amber-300 rounded-full font-semibold text-sm disabled:opacity-40">
              <Clock size={16} className="mr-1.5" /> {scheduling ? "Programmazione..." : "Programma invio"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!previewHtml} onOpenChange={(open) => !open && setPreviewHtml(null)}>
        <DialogContent className="bg-surface border-white/10 text-white sm:max-w-3xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="newsletter-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading tracking-tight text-xl flex items-center gap-2">
              <Eye size={20} className="text-amber-400" /> Anteprima newsletter
            </DialogTitle>
          </DialogHeader>
          <iframe title="Anteprima newsletter" srcDoc={previewHtml || ""} sandbox=""
            className="w-full flex-1 min-h-[65vh] rounded-lg border border-white/10 bg-white" />
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent className="bg-surface border-white/10 text-white" data-testid="newsletter-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Confermi l'invio della newsletter?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              "{subject}" verrà inviata a {recipientList.length} destinatari tramite l'SMTP EmailMarketing. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="newsletter-confirm-cancel" className="bg-transparent border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="newsletter-confirm-send" onClick={handleSend} className="bg-amber-400 text-black hover:bg-amber-300 rounded-full font-semibold">
              Invia ora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
